import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  toast,
} from '@saas-kit/ui';
import { DataTable, type PaginationState } from '@saas-kit/ui/DataTable';

import {
  archivePlan,
  createPlan,
  listPlans,
  updatePlan,
  type AdminPlan,
  type CreatePlanPayload,
  type UpdatePlanPayload,
} from './api';
import { PlanFormDialog } from './PlanFormDialog';
import { usePlanColumns } from './usePlanColumns';

const DEFAULT_PAGE_SIZE = 25;

type DialogMode = { type: 'create' } | { type: 'edit'; plan: AdminPlan } | null;

export function PlansPage(): JSX.Element {
  const { t } = useTranslation('adminPlans');
  const queryClient = useQueryClient();

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dialog, setDialog] = useState<DialogMode>(null);
  const [confirming, setConfirming] = useState<AdminPlan | null>(null);

  const plansQuery = useQuery({
    queryKey: ['admin-plans', { offset, limit: pageSize }],
    queryFn: () => listPlans({ offset, limit: pageSize, include_inactive: true }),
    placeholderData: (prev) => prev,
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
  }

  const createMutation = useMutation({
    mutationFn: (payload: CreatePlanPayload) => createPlan(payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.created'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePlanPayload }) =>
      updatePlan(id, payload),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.updated'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  const archiveMutation = useMutation({
    mutationFn: (plan: AdminPlan) => archivePlan(plan.id),
    onSuccess: async () => {
      await invalidate();
      toast.success(t('toasts.archived'));
    },
    onError: (err) => toast.error(parseApiError(err).message),
  });

  const handleEdit = useCallback(
    (plan: AdminPlan) => setDialog({ type: 'edit', plan }),
    [],
  );
  const handleArchive = useCallback(
    (plan: AdminPlan) => setConfirming(plan),
    [],
  );
  const columns = usePlanColumns({ onEdit: handleEdit, onArchive: handleArchive });

  const plans = plansQuery.data?.items ?? [];
  const page = plansQuery.data?.page;
  const total = page?.total ?? 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext: page ? page.offset + page.limit < page.total : false,
    hasPrev: offset > 0,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const showEmpty =
    !plansQuery.isLoading && !plansQuery.isError && total === 0;

  async function handleSubmit(
    payload: CreatePlanPayload | UpdatePlanPayload,
  ): Promise<void> {
    if (!dialog) return;
    if (dialog.type === 'create') {
      await createMutation.mutateAsync(payload as CreatePlanPayload);
    } else {
      await updateMutation.mutateAsync({
        id: dialog.plan.id,
        payload,
      });
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Button onClick={() => setDialog({ type: 'create' })}>
            {t('actions.create')}
          </Button>
        }
      />

      {plansQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          data={plans}
          columns={columns}
          rowKey={(row) => row.id}
          testId="admin-plans"
          selectable
          pagination={pagination}
          isLoading={plansQuery.isLoading}
          error={plansQuery.isError ? t('errors.load_failed') : null}
          exportable
          exportFilename="admin-plans"
        />
      )}

      {dialog ? (
        <PlanFormDialog
          open
          mode={dialog.type}
          plan={dialog.type === 'edit' ? dialog.plan : null}
          isPending={createMutation.isPending || updateMutation.isPending}
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title={t('confirm.archive_title', { name: confirming.name })}
          body={t('confirm.archive_body')}
          confirmLabel={t('actions.archive')}
          variant="destructive"
          isBusy={archiveMutation.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            archiveMutation.mutate(confirming, {
              onSuccess: () => setConfirming(null),
            });
          }}
        />
      ) : null}
    </div>
  );
}
